import { toast } from "sonner";

/**
 * Best-effort native browser download. Use `downloadViaUrl` whenever the
 * server can return a one-shot token URL — that path bypasses iframe sandbox
 * restrictions and always saves to the user's Downloads folder.
 */
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

export function downloadViaUrl(relativeOrAbsoluteUrl, filename) {
  const url = relativeOrAbsoluteUrl.startsWith("http")
    ? relativeOrAbsoluteUrl
    : `${BACKEND_URL}${relativeOrAbsoluteUrl}`;

  // Primary strategy: real <a href> anchor with download attribute.
  // Because the URL is a same-origin HTTPS endpoint that returns
  // Content-Disposition: attachment, the browser saves the file
  // to the user's default Downloads folder — no popup, no iframe.
  const a = document.createElement("a");
  a.href = url;
  if (filename) a.download = filename;
  a.rel = "noopener";
  a.style.display = "none";
  document.body.appendChild(a);

  let triggered = false;
  try {
    a.click();
    triggered = true;
  } catch (err) { if (process.env.NODE_ENV !== "production") console.warn("[dv] swallowed:", err); }

  setTimeout(() => {
    try { a.remove(); } catch (err) { if (process.env.NODE_ENV !== "production") console.warn("[dv] swallowed:", err); }
  }, 2000);

  if (triggered) return true;

  // Fallback: open URL in a new tab if anchor click is blocked
  const w = window.open(url, "_blank", "noopener");
  if (!w) {
    toast.error("Browser blocked the download. Try right-clicking the XLSX/CSV button and choose 'Open in new tab'.");
    return false;
  }
  return true;
}

/**
 * Trigger a browser download for a blob (client-side built). Used for small
 * text scripts where round-tripping the server is overkill.
 */
export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  a.style.display = "none";
  document.body.appendChild(a);

  let triggered = false;
  try {
    a.click();
    triggered = true;
  } catch (err) { if (process.env.NODE_ENV !== "production") console.warn("[dv] swallowed:", err); }

  if (!triggered) {
    const w = window.open(url, "_blank", "noopener,noreferrer");
    if (!w) {
      toast.error("Browser blocked the download. Allow pop-ups or open this site in a new tab.");
    }
  }

  setTimeout(() => {
    try { a.remove(); } catch (err) { if (process.env.NODE_ENV !== "production") console.warn("[dv] swallowed:", err); }
    URL.revokeObjectURL(url);
  }, 2000);
}

export function safeName(s, fallback = "file") {
  return (s || fallback).toString().replace(/[^A-Za-z0-9_.\-]+/g, "_").slice(0, 120) || fallback;
}
